import { QueryClient } from "@tanstack/react-query";

/**
 * Singleton QueryClient
 *
 * WHY A SEPARATE FILE:
 * ────────────────────
 * If you put `new QueryClient()` inside a component, React re-creates it on
 * every render — wiping the entire cache. Moving it here makes it a module-
 * level singleton: one instance, shared across the whole app, for its lifetime.
 *
 * DEFAULT OPTIONS RATIONALE:
 * ──────────────────────────
 * staleTime: 2 min  → Most CRM data doesn't change second-by-second.
 *                     2 min means navigating back to a page shows instant
 *                     cached data instead of a spinner, then silently
 *                     refetches in the background.
 *
 * gcTime: 10 min    → Inactive cache entries (no subscriber) live for 10 min.
 *                     User leaves Customers page → goes to Orders → comes back:
 *                     the Customers cache is still warm. No spinner.
 *
 * retry: 1          → Retry once on network failure (e.g. brief disconnect).
 *                     Don't retry 4xx errors — they won't succeed on retry.
 *
 * refetchOnWindowFocus: false
 *                   → In a CRM/ERP, users often have many tabs open. Aggressive
 *                     refetch on focus causes floods of requests. Turn it off;
 *                     rely on manual invalidation after mutations instead.
 *
 * Per-query overrides:
 *   You can always override these defaults in individual useQuery() calls
 *   via the options object: useQuery({ ..., staleTime: 0 }) for real-time data.
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 2 * 60_000,       // 2 min — data is "fresh" for 2 minutes
            gcTime:    10 * 60_000,       // 10 min — keep unused cache entries
            retry:     1,
            refetchOnWindowFocus:  false,
            refetchOnReconnect:    true,  // do refetch if internet was lost
            refetchOnMount:        true,  // refetch when component mounts (if stale)
        },
        mutations: {
            retry: 0,                     // never auto-retry mutations
        },
    },
});
